import { Flex, FlexProps, forwardRef } from '@chakra-ui/react';

import {
  NAVIGATION_RAIL_EXPANDED_WIDTH,
  NAVIGATION_RAIL_WIDTH,
  useNavigationRailStore,
} from '../NavigationRail/NavigationRail.store';

// react-focus-lock renders this as the actual DOM node it locks focus into (via its `as`
// prop) and only ever forwards `ref`/`className`/`onBlur`/`onFocus` to it - no style props -
// so the drawer's look has to live inside the component rather than passed in at the call site.
export const NavigationDrawer = forwardRef<FlexProps, 'nav'>((props, ref) => {
  const isExpanded = useNavigationRailStore((state) => state.isExpanded);
  const shouldAnimate = useNavigationRailStore((state) => state.shouldAnimate);

  return (
    <Flex
      as="nav"
      ref={ref}
      minW={NAVIGATION_RAIL_WIDTH}
      position={{ base: 'fixed', lg: 'absolute' }}
      left={0}
      top={{ base: 0, lg: '100%' }}
      flexDir="column"
      // on desktop the width is the rail's own - `max-content` would jump to the widest label
      w={{ base: 'min(100%, 15rem)', lg: isExpanded ? NAVIGATION_RAIL_EXPANDED_WIDTH : NAVIGATION_RAIL_WIDTH }}
      h={{ base: '100%', lg: 'calc(100vh - 100%)' }}
      py={{ base: 6, lg: 4 }}
      px={{ base: 6, lg: 2 }}
      overflowY="auto"
      // clips the labels while the rail is still widening, so they are revealed rather than
      // spilling out - and keeps their fixed width from producing a horizontal scrollbar
      overflowX="hidden"
      overscrollBehaviorY="contain"
      bg="white"
      zIndex={1}
      fontSize="0.875rem"
      lineHeight="calc(20/14)"
      boxShadow="inset -1px 0 0 #CCC"
      visibility={{ base: 'hidden', lg: 'visible' }}
      transform={{ base: 'translateX(-110vw)', lg: 'initial' }}
      willChange="transform"
      transition={{
        base: 'transform .3s ease, visibility 0s linear .3s',
        lg: shouldAnimate ? 'width .2s ease' : 'none',
      }}
      _expanded={{
        visibility: 'visible',
        transform: 'translateX(0)',
        transition: 'transform .3s ease',
      }}
      {...props}
    />
  );
});
